import React, { useEffect, useRef, useState } from "react";
import {
  StyledManagementTable,
  StyledManagementTh,
  StyledManagementTr,
  StyledManagementTd,
  ScrollableTableContainer,
  StyledTableLoadingContainer,
} from "./ManagementTable.styled";
import { BodyTyp } from "../../../../components/Typography/Typography";
import { FontWeightEnum, IconSizeEnum } from "../../../../types/AppTypes";
import {
  StyledManagementLinkHeader,
  StyledManagementPage,
} from "../../Management.styled";
import { ReactComponent as AddIcon } from "../../../../icons/add.svg";
import { ReactComponent as GridIcon } from "../../../../icons/dotsGrid.svg";
import { ReactComponent as DeleteIcon } from "../../../../icons/bin.svg";
import { ReactComponent as CancelIcon } from "../../../../icons/close.svg";
import { ReactComponent as SaveIcon } from "../../../../icons/save.svg";
import { ReactComponent as EditIcon } from "../../../../icons/pencil.svg";
import { Link, LinkSizeEnum } from "../../../../components/core/Link/Link";
import { PopupConfirm } from "../../../../components/PopupConfirm/PopupConfirm";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "@hello-pangea/dnd";
import {
  CANCEL_LABEL,
  DELETE_LABEL,
  EDIT_LABEL,
  FIELD_IS_REQUIRED_LABEL,
  NO_DATA_LABEL,
  ORDER_LABEL,
  SAVE_LABEL,
} from "../../../../constants/Dictionary";
import { IconManager } from "../../../../components/core/IconManager/IconManager";
import { theme } from "../../../../constants/StyledComponents";
import { BadgeIndex } from "../../../../components/BadgeIndex/BadgeIndex";
import TextInput from "../../../../components/core/TextInput/TextInput";
import {
  Controller,
  FieldValues,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { StyledManagementFooter } from "../Footer/ManagementFooter.styled";
import {
  Button,
  ButtonVariants,
} from "../../../../components/core/Button/Button";
import { FormField } from "../../../../components/core/FormField/FormField";
import { SelectRange } from "../../../../components/core/SelectRange/SelectRange";
import { Loader } from "../../../../components/core/Loader/Loader";
import { MessageBox } from "../../../../components/MessageBox/MessageBox";
import { StyledAlertMessage } from "../../../Formation/Courses/CourseModal/CourseModal.styled";
import { ReactComponent as WarningIcon } from "../../../../icons/warning.svg";
import { useEditing } from "../../../../providers/editing.provider";

export type ManagementTableHeader = {
  key: string;
  label: string;
  width: number;
  type: "text" | "range";
};

type Item = { [key: string]: unknown };

type FormValues = {
  items: Item[];
};

type ManagementTableProps<T extends Item> = {
  headers: ManagementTableHeader[];
  data: T[];
  itemLabel: string;
  keyItem: keyof T;
  draggableRows: boolean;
  toggleFilters?: (x: boolean) => void;
  onSave: (data: FieldValues) => void;
  loading: boolean;
  orderColumnName?: string;
  customTest?: (data: FieldValues) => { result: boolean; message: string };
};

export const ManagementTable = <T extends Item>({
  headers,
  data,
  itemLabel,
  keyItem,
  draggableRows,
  toggleFilters,
  onSave,
  loading,
  orderColumnName,
  customTest,
}: ManagementTableProps<T>) => {
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const { edit, setEdit } = useEditing();
  const [isNew, setIsNew] = useState<boolean>(false);
  const [rangeError, setRangeError] = useState<string>("");
  const widthDraggableColumn = 5;
  const widthOrderColumn = 10;
  const widthActionsColumn = 5;

  const endRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "items",
    keyName: "rowId",
  });

  const addNewRow = () => {
    setIsNew(true);
    const emptyRow = Object.fromEntries(
      headers.map((header) => [header.key, null]),
    );
    append(emptyRow);
  };

  const toggleEdit = () => {
    setEdit(!edit);
    toggleFilters ? toggleFilters(!edit) : null;
  };

  const cancelEdit = () => {
    reset();
    toggleEdit();
    setRangeError("");
  };

  const onSubmit = (data: FormValues) => {
    if (customTest) {
      const test = customTest(data);
      if (!test.result) {
        setRangeError(test.message);
        return;
      }
    }

    setRangeError("");
    onSave(data);
    toggleEdit();
  };

  const handleDelete = (index: number) => {
    setSelectedRowIndex(index);
    setOpenPopup(true);
  };

  const removeRow = () => {
    if (selectedRowIndex === null) {
      return;
    }
    setOpenPopup(false);
    remove(selectedRowIndex);
    setSelectedRowIndex(null);
  };

  const getPopupText = () => {
    if (selectedRowIndex === null) {
      return "";
    }

    const field = fields[selectedRowIndex] as unknown as T;
    return `Stai eliminando ${field[keyItem] || "un elemento"}. Vuoi procedere?`;
  };

  const onDragRowsEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    move(result.source.index, result.destination.index);
  };

  const calculateHeaderWith = (headerWith: number) => {
    const fixedColumnsWidth =
      (draggableRows ? widthDraggableColumn + widthOrderColumn : 0) +
      widthActionsColumn;
    return ((100 - fixedColumnsWidth) * headerWith) / 100;
  };

  const getColGroups = () => {
    return (
      <colgroup>
        {draggableRows && <col width={`${widthDraggableColumn}%`} />}
        {draggableRows && <col width={`${widthOrderColumn}%`} />}
        {headers.map((header, index) => (
          <col key={index} width={`${calculateHeaderWith(header.width)}%`} />
        ))}
        <col width={`${widthActionsColumn}%`} />
      </colgroup>
    );
  };

  useEffect(() => {
    if (isNew) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
      setIsNew(false);
    }
  }, [isNew]);

  useEffect(() => {
    if (data) {
      reset({ items: data });
    }
  }, [data]);

  return (
    <>
      <StyledManagementPage>
        <StyledManagementLinkHeader>
          {edit && (
            <Link
              size={LinkSizeEnum.SMALL}
              onClick={addNewRow}
              icon={<AddIcon />}
            >
              {itemLabel}
            </Link>
          )}
        </StyledManagementLinkHeader>
        <form
          className="management-content"
          id="managementForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <StyledManagementTable>
            {getColGroups()}
            <thead>
              <tr>
                {draggableRows && (
                  <>
                    <StyledManagementTh key="dragColumn" />
                    <StyledManagementTh key="orderColumn">
                      <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                        {orderColumnName ?? ORDER_LABEL}
                      </BodyTyp>
                    </StyledManagementTh>
                  </>
                )}
                {headers.map((column) => {
                  return (
                    <StyledManagementTh key={column.key}>
                      <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                        {column.label}
                      </BodyTyp>
                    </StyledManagementTh>
                  );
                })}
                <StyledManagementTh key="actionColumn" />
              </tr>
            </thead>
          </StyledManagementTable>
          <DragDropContext onDragEnd={onDragRowsEnd}>
            <Droppable droppableId="droppable">
              {(provided) =>
                loading ? (
                  <StyledTableLoadingContainer>
                    <Loader loading={loading} />
                  </StyledTableLoadingContainer>
                ) : (
                  <ScrollableTableContainer>
                    <StyledManagementTable
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {getColGroups()}
                      <tbody>
                        {fields.length > 0 ? (
                          fields.map((field, rowIndex) => {
                            return (
                              <Draggable
                                isDragDisabled={!edit}
                                key={field.rowId}
                                draggableId={field.rowId}
                                index={rowIndex}
                              >
                                {(provided, snapshot) => (
                                  <StyledManagementTr
                                    key={rowIndex}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    $isDragging={snapshot.isDragging}
                                    {...provided.draggableProps.style}
                                  >
                                    {draggableRows && (
                                      <>
                                        <StyledManagementTd
                                          key="dragColumn"
                                          style={{ textAlign: "center" }}
                                          width={`${widthDraggableColumn}%`}
                                        >
                                          <div
                                            className="drag-handle"
                                            {...provided.dragHandleProps}
                                          >
                                            {edit && (
                                              <IconManager
                                                color={theme.colors.purple}
                                                size={IconSizeEnum.SMALL}
                                              >
                                                <GridIcon />
                                              </IconManager>
                                            )}
                                          </div>
                                        </StyledManagementTd>
                                        <StyledManagementTd
                                          key="orderColumn"
                                          width={`${widthOrderColumn}%`}
                                        >
                                          <BadgeIndex
                                            content={(rowIndex + 1).toString()}
                                          />
                                        </StyledManagementTd>
                                      </>
                                    )}
                                    {headers.map((column) => {
                                      return (
                                        <StyledManagementTd
                                          key={column.key}
                                          width={`${calculateHeaderWith(column.width)}%`}
                                        >
                                          {edit ? (
                                            <>
                                              {column.type === "text" && (
                                                <Controller
                                                  render={({
                                                    field,
                                                    fieldState,
                                                  }) => (
                                                    <FormField
                                                      error={
                                                        fieldState.error
                                                          ?.message
                                                      }
                                                      fixedError={false}
                                                    >
                                                      <TextInput
                                                        maxLength={255}
                                                        onChange={
                                                          field.onChange
                                                        }
                                                        value={
                                                          field.value as string
                                                        }
                                                      />
                                                    </FormField>
                                                  )}
                                                  name={`items.${rowIndex}.${column.key}`}
                                                  control={control}
                                                  rules={{
                                                    required: {
                                                      value: true,
                                                      message:
                                                        FIELD_IS_REQUIRED_LABEL,
                                                    },
                                                  }}
                                                />
                                              )}
                                              {column.type === "range" && (
                                                <Controller
                                                  render={({
                                                    field,
                                                    fieldState,
                                                  }) => (
                                                    <FormField
                                                      error={
                                                        fieldState.error
                                                          ?.message
                                                      }
                                                      fixedError={false}
                                                    >
                                                      <SelectRange
                                                        value={
                                                          field.value
                                                            ? {
                                                                min: (
                                                                  field.value as number[]
                                                                )[0],
                                                                max: (
                                                                  field.value as number[]
                                                                )[1],
                                                              }
                                                            : undefined
                                                        }
                                                        onChange={(value) =>
                                                          field.onChange([
                                                            value.min,
                                                            value.max,
                                                          ])
                                                        }
                                                      />
                                                    </FormField>
                                                  )}
                                                  name={`items.${rowIndex}.${column.key}`}
                                                  control={control}
                                                  rules={{
                                                    required: {
                                                      value: true,
                                                      message:
                                                        FIELD_IS_REQUIRED_LABEL,
                                                    },
                                                  }}
                                                />
                                              )}
                                            </>
                                          ) : (
                                            <BodyTyp
                                              variant="1"
                                              fontWeight={
                                                FontWeightEnum.SEMIBOLD
                                              }
                                              color={theme.colors.purple}
                                            >
                                              {column.type === "text" &&
                                                (fields[rowIndex][
                                                  column.key
                                                ] as string)}
                                              {column.type === "range" &&
                                              fields[rowIndex][column.key]
                                                ? `${(fields[rowIndex][column.key] as number[])[0]} - ${(fields[rowIndex][column.key] as number[])[1]}%`
                                                : ""}
                                            </BodyTyp>
                                          )}
                                        </StyledManagementTd>
                                      );
                                    })}
                                    <StyledManagementTd
                                      key="actionColumn"
                                      style={{ textAlign: "center" }}
                                      width={`${widthActionsColumn}%`}
                                    >
                                      {edit && !snapshot.isDragging && (
                                        <IconManager
                                          color={theme.colors.purple}
                                          size={IconSizeEnum.MEDIUM}
                                          onClick={() => handleDelete(rowIndex)}
                                        >
                                          <DeleteIcon />
                                        </IconManager>
                                      )}
                                    </StyledManagementTd>
                                  </StyledManagementTr>
                                )}
                              </Draggable>
                            );
                          })
                        ) : (
                          <StyledManagementTr $isDragging={false}>
                            {!edit && (
                              <StyledManagementTd colSpan={6}>
                                <MessageBox message={NO_DATA_LABEL} />
                              </StyledManagementTd>
                            )}
                          </StyledManagementTr>
                        )}
                        <div ref={endRef} />
                      </tbody>
                    </StyledManagementTable>
                  </ScrollableTableContainer>
                )
              }
            </Droppable>
          </DragDropContext>
        </form>
        <StyledManagementFooter>
          {rangeError && (
            <StyledAlertMessage>
              <IconManager size={IconSizeEnum.SMALL} color={theme.colors.red_1}>
                <WarningIcon />
              </IconManager>
              <BodyTyp
                color={theme.colors.red_1}
                fontWeight={FontWeightEnum.SEMIBOLD}
              >
                {rangeError}
              </BodyTyp>
            </StyledAlertMessage>
          )}
          {edit && (
            <Button
              label={CANCEL_LABEL}
              icon={<CancelIcon />}
              onClick={cancelEdit}
              variant={ButtonVariants.SECONDARY}
            />
          )}
          {edit && (
            <Button
              label={SAVE_LABEL}
              icon={<SaveIcon />}
              type="submit"
              form="managementForm"
              disabled={!isDirty}
            />
          )}
          {!edit && (
            <Button
              label={EDIT_LABEL}
              icon={<EditIcon />}
              onClick={toggleEdit}
              type="button"
            />
          )}
        </StyledManagementFooter>
      </StyledManagementPage>
      <PopupConfirm
        isOpen={openPopup}
        label={`${DELETE_LABEL} ${itemLabel}`}
        popupText={getPopupText()}
        onConfirm={() => removeRow()}
        onCancel={() => {
          setSelectedRowIndex(null);
          setOpenPopup(false);
        }}
      />
    </>
  );
};
